import React, { useEffect } from 'react';
import { 
  X, 
  Heart, 
  Share2, 
  Download, 
  ExternalLink, 
  Clock, 
  User, 
  Calendar,
  Tag,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useResourceStore } from '../../store/resourceStore';
import type { Resource } from '../../types/resources';

interface ResourceViewerProps {
  resource: Resource;
  onClose: () => void;
  onLike?: (resourceId: string) => void;
  isLiked?: boolean;
}

export const ResourceViewer: React.FC<ResourceViewerProps> = ({
  resource,
  onClose,
  onLike,
  isLiked = false
}) => {
  const { recordInteraction } = useResourceStore();

  useEffect(() => {
    // Record view interaction
    recordInteraction(resource.id, 'VIEW');
  }, [resource.id, recordInteraction]);

  const handleLike = () => {
    if (onLike) {
      onLike(resource.id);
      recordInteraction(resource.id, 'LIKE');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: resource.title,
          text: resource.description,
          url: window.location.href
        });
        recordInteraction(resource.id, 'SHARE');
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      recordInteraction(resource.id, 'SHARE');
    }
  };

  const handleDownload = () => {
    if (resource.file_url) {
      window.open(resource.file_url, '_blank');
      recordInteraction(resource.id, 'DOWNLOAD');
    }
  };

  const handleExternalLink = () => {
    if (resource.external_url) {
      window.open(resource.external_url, '_blank');
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels = {
      'BUSINESS_MODEL': 'Modèle Économique',
      'LEGAL_STRUCTURE': 'Statut Juridique',
      'FINANCING': 'Financement',
      'MARKETING': 'Marketing',
      'MANAGEMENT': 'Gestion',
      'IMPACT_MEASUREMENT': 'Mesure d\'Impact',
      'NETWORKING': 'Réseautage',
      'DIGITAL_TOOLS': 'Outils Numériques',
      'SUSTAINABILITY': 'Développement Durable',
      'INNOVATION': 'Innovation Sociale'
    };
    return labels[category as keyof typeof labels] || category;
  };

  const getDifficultyColor = (difficulty: string) => {
    const colors = {
      'BEGINNER': 'bg-green-100 text-green-800',
      'INTERMEDIATE': 'bg-yellow-100 text-yellow-800',
      'ADVANCED': 'bg-orange-100 text-orange-800',
      'EXPERT': 'bg-red-100 text-red-800'
    };
    return colors[difficulty as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getDifficultyLabel = (difficulty: string) => {
    const labels = {
      'BEGINNER': 'Débutant',
      'INTERMEDIATE': 'Intermédiaire',
      'ADVANCED': 'Avancé',
      'EXPERT': 'Expert'
    };
    return labels[difficulty as keyof typeof labels] || difficulty;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1 mr-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{resource.title}</h1>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <User className="w-4 h-4" />
                <span>{resource.author_name}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(resource.created_at), 'dd MMMM yyyy', { locale: fr })}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{resource.estimated_reading_time} min de lecture</span>
              </div>
              <div className="flex items-center space-x-1">
                <Eye className="w-4 h-4" />
                <span>{resource.view_count} vues</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Actions */}
            {onLike && (
              <button
                onClick={handleLike}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${
                  isLiked 
                    ? 'text-red-600 bg-red-50 hover:bg-red-100' 
                    : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
                }`}
              >
                <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                <span>{resource.like_count}</span>
              </button>
            )}
            
            <button
              onClick={handleShare}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Partager"
            >
              <Share2 className="w-4 h-4" />
            </button>
            
            {resource.file_url && (
              <button
                onClick={handleDownload}
                className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Télécharger"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            
            {resource.external_url && (
              <button
                onClick={handleExternalLink}
                className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="Lien externe"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
            )}
            
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Meta info */}
            <div className="flex items-center space-x-4 mb-6">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                {getCategoryLabel(resource.category)}
              </span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getDifficultyColor(resource.difficulty_level)}`}>
                {getDifficultyLabel(resource.difficulty_level)}
              </span>
            </div>

            {/* Description */}
            <div className="mb-6">
              <p className="text-gray-700 text-lg leading-relaxed">{resource.description}</p>
            </div>

            {/* Tags */}
            {resource.tags.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Tag className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Mots-clés</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {resource.tags.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-md">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="prose prose-blue max-w-none">
              <div 
                className="text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: resource.content.replace(/\n/g, '<br>').replace(/#{1,6}\s/g, '<strong>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};